import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import StatusBadge from '../components/StatusBadge';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Separator } from '../components/ui/separator';
import { ArrowLeft, ChevronRight, ImagePlus, Phone, Mail, MapPin, Wrench, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_OPTIONS = [
  { v: 'new', l: 'New' },
  { v: 'scheduled', l: 'Scheduled' },
  { v: 'assigned', l: 'Assigned' },
  { v: 'in_progress', l: 'In Progress' },
  { v: 'waiting_parts', l: 'Waiting on Parts' },
  { v: 'completed', l: 'Completed' },
  { v: 'cancelled', l: 'Cancelled' },
];

export default function TicketDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { tickets, customers, devices, technicians, alerts, updateTicketStatus, addTicketNote } = useApp();
  const [note, setNote] = useState('');
  const [checked, setChecked] = useState({});

  const t = tickets.find((x) => x.id === id);
  if (!t) {
    return (
      <div className="text-center py-20">
        <div className="text-slate-500">Ticket not found.</div>
        <Button className="mt-4" onClick={() => navigate('/tickets')}>Back</Button>
      </div>
    );
  }

  const customer = customers.find((c) => c.id === t.customerId);
  const device = devices.find((d) => d.id === t.deviceId);
  const tech = technicians.find((x) => x.id === t.technician);
  const alert = alerts.find((a) => a.id === t.alertSource);

  return (
    <div className="space-y-6" data-testid="ticket-detail-page">
      <div>
        <div className="flex items-center gap-2 text-xs text-slate-500">
          <button onClick={() => navigate('/tickets')} className="hover:text-slate-700 inline-flex items-center gap-1">
            <ArrowLeft className="h-3 w-3" /> Tickets
          </button>
          <ChevronRight className="h-3 w-3" />
          <span className="font-mono">{t.id}</span>
        </div>
        <div className="mt-3 flex items-start justify-between gap-6 flex-wrap">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Service Ticket</div>
            <h1 className="mt-1 text-3xl font-semibold text-slate-900 tracking-tight">{t.issue}</h1>
            <div className="mt-1 text-sm text-slate-500 font-mono">{t.id} · created {new Date(t.createdAt).toDateString()}</div>
            <div className="mt-3 flex items-center gap-2 flex-wrap">
              <StatusBadge status={t.status} />
              <StatusBadge status={t.priority === 'High' ? 'critical' : t.priority === 'Medium' ? 'warning' : 'info'}>{t.priority} priority</StatusBadge>
              <span className="font-mono text-xs text-slate-500">Est. ${t.estRevenue}</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Select value={t.status} onValueChange={(v) => { updateTicketStatus(t.id, v); toast.success('Status updated'); }}>
              <SelectTrigger className="w-[200px] h-9" data-testid="ticket-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => { updateTicketStatus(t.id, 'completed'); toast.success('Ticket marked complete'); }} data-testid="ticket-complete">
              <CheckCircle2 className="h-4 w-4 mr-2" /> Mark Complete
            </Button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <Card className="p-5 border-slate-200 shadow-sm">
          <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">Customer</div>
          {customer && (
            <div className="mt-3">
              <Link to={`/customers/${customer.id}`} className="font-semibold text-slate-900 hover:text-[#0052CC]">{customer.name}</Link>
              <div className="mt-1 text-xs text-slate-600 flex items-center gap-1"><MapPin className="h-3 w-3" /> {customer.address}, {customer.city}</div>
              <div className="mt-0.5 text-xs text-slate-600 font-mono flex items-center gap-1"><Phone className="h-3 w-3" /> {customer.phone}</div>
              <div className="mt-0.5 text-xs text-slate-600 font-mono flex items-center gap-1"><Mail className="h-3 w-3" /> {customer.email}</div>
              <Separator className="my-3" />
              <div className="text-[11px] uppercase tracking-wider text-slate-500">Plan</div>
              <div className="mt-1"><StatusBadge status={customer.plan === 'Premium Comfort Protection' ? 'warning' : customer.plan === 'Preventive Maintenance' ? 'healthy' : 'info'}>{customer.plan}</StatusBadge></div>
            </div>
          )}
        </Card>

        <Card className="p-5 border-slate-200 shadow-sm">
          <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">Device</div>
          {device && (
            <div className="mt-3">
              <Link to={`/devices/${device.id}`} className="font-semibold text-slate-900 hover:text-[#0052CC]">{device.model}</Link>
              <div className="mt-0.5 text-xs font-mono text-slate-500">{device.id}</div>
              <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                <div>Indoor: <span className="font-mono font-semibold text-slate-900">{device.indoorTemp}°</span></div>
                <div>Setpoint: <span className="font-mono font-semibold text-slate-900">{device.setpoint}°</span></div>
                <div>Humidity: <span className="font-mono font-semibold text-slate-900">{device.humidity}%</span></div>
                <div>Health: <span className="font-mono font-semibold text-slate-900">{device.healthScore}/100</span></div>
              </div>
            </div>
          )}
        </Card>

        <Card className="p-5 border-slate-200 shadow-sm">
          <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">Assignment</div>
          {tech && (
            <div className="mt-3 flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-slate-900 text-white text-xs font-semibold flex items-center justify-center">{tech.initials}</div>
              <div>
                <div className="font-semibold text-slate-900 text-sm">{tech.name}</div>
                <div className="text-[11px] text-slate-500">{tech.role} · rating {tech.rating}</div>
              </div>
            </div>
          )}
          <Separator className="my-3" />
          <div className="text-[11px] uppercase tracking-wider text-slate-500">Scheduled</div>
          <div className="mt-1 font-mono text-sm text-slate-900">{t.scheduledAt}</div>
          <Separator className="my-3" />
          <div className="text-[11px] uppercase tracking-wider text-slate-500">Est. Revenue</div>
          <div className="mt-1 font-mono text-xl font-semibold text-slate-900">${t.estRevenue}</div>
        </Card>
      </div>

      {alert && (
        <Card className="p-5 border-slate-200 shadow-sm border-l-4 border-l-[#0052CC]">
          <div className="text-xs uppercase tracking-[0.18em] text-[#0052CC] font-semibold">Source Alert</div>
          <div className="mt-2 font-semibold text-slate-900">{alert.type}</div>
          <div className="mt-1 text-sm text-slate-600">{alert.aiSummary}</div>
          <div className="mt-2 rounded-md bg-slate-50 border border-slate-200 p-2.5 text-xs">
            <span className="font-semibold text-slate-900">Recommended:</span> {alert.recommended}
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <Card className="p-5 border-slate-200 shadow-sm">
          <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">Technician Checklist</div>
          <div className="mt-3 space-y-2.5">
            {t.checklist.map((item, i) => (
              <label key={i} className="flex items-start gap-3 cursor-pointer">
                <Checkbox
                  checked={!!checked[i]}
                  onCheckedChange={(v) => setChecked((c) => ({ ...c, [i]: v }))}
                  className="mt-0.5"
                />
                <span className={`text-sm ${checked[i] ? 'text-slate-400 line-through' : 'text-slate-800'}`}>{item}</span>
              </label>
            ))}
          </div>
          <Separator className="my-5" />
          <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">Parts Needed</div>
          <div className="mt-2 flex flex-wrap gap-2">
            {t.partsNeeded.length ? t.partsNeeded.map((p, i) => (
              <span key={i} className="px-2.5 py-1 rounded-full bg-slate-100 text-xs font-mono text-slate-700 border border-slate-200">{p}</span>
            )) : <span className="text-xs text-slate-500">None listed</span>}
          </div>
        </Card>

        <Card className="p-5 border-slate-200 shadow-sm">
          <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">Before / After Photos</div>
          <div className="mt-3 grid grid-cols-2 gap-2">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="aspect-square rounded-md border-2 border-dashed border-slate-200 flex items-center justify-center text-slate-400 hover:border-slate-300 transition-colors cursor-pointer">
                <div className="text-center">
                  <ImagePlus className="h-5 w-5 mx-auto" />
                  <div className="text-[10px] mt-1">{i < 2 ? 'Before' : 'After'}</div>
                </div>
              </div>
            ))}
          </div>
          <Separator className="my-5" />
          <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">Resolution Summary</div>
          {t.resolution ? (
            <div className="mt-2 rounded-md bg-emerald-50 border border-emerald-200 p-3 text-sm text-slate-800">{t.resolution}</div>
          ) : (
            <Textarea placeholder="Summarize repair steps, replaced parts, follow-up recommendations..." className="mt-2" />
          )}
        </Card>
      </div>

      <Card className="p-5 border-slate-200 shadow-sm">
        <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">Internal Notes</div>
        <div className="mt-3 space-y-3">
          {t.notes.map((n, i) => (
            <div key={i} className="flex items-start gap-3 p-3 rounded-md border border-slate-200 bg-slate-50/60">
              <div className="h-7 w-7 rounded-full bg-slate-900 text-white text-[10px] font-semibold flex items-center justify-center">{n.author.slice(0, 2)}</div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold text-slate-800">{n.author}</span>
                  <span className="text-[11px] text-slate-500">{n.ts}</span>
                </div>
                <div className="mt-0.5 text-sm text-slate-700">{n.text}</div>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-4">
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add internal note (not visible to customer)..." data-testid="ticket-note-input" />
          <div className="mt-2 flex justify-end gap-2">
            <Button variant="outline" onClick={() => { toast.success('Customer notification sent'); }}>Notify Customer</Button>
            <Button onClick={() => { if (!note.trim()) return; addTicketNote(t.id, note); toast.success('Note added'); setNote(''); }} className="bg-[#0052CC] hover:bg-[#0043A6]" data-testid="ticket-note-submit">
              <Wrench className="h-4 w-4 mr-2" /> Add Note
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
